import { Icon } from "../../atoms/icons";
import { Typography } from "../../atoms/typography";
import { cn, platformProps } from "../../lib";
import { minTouchTargetClassName } from "../../lib/touch-target";
import { Box, Pressable } from "../../primitives";
import { iconSize, lineHeight, spacing, touchTarget } from "../../tokens";
import { getLeadingIconSpec } from "./lib";
import type { ToastOptions } from "./types";

export interface ToastContentProps extends ToastOptions {
  onDismiss: () => void;
  testID?: string;
}

// Figma draws the action as its `label-sm` label (20 tall on phones) and the
// close as a bare 16px ×, 12 apart. Each takes taps 44 tall without growing:
// native reaches 12 above and below the action and 14 around the close, web
// touch screens an invisible area (`minTouchTargetClassName`). Beside each
// other, each reaches only half the 12px gap, so neither takes the other's
// taps, and makes up 44 across on its outer side: the close 22 to its right,
// the action 22 to its left, over the text (the action is at least 16 wide).
// Without the close (`dismissible: false`) the action's area is centred on it.
const CLOSE_SLOP = (touchTarget - iconSize.sm) / 2;
const ACTION_VERTICAL_SLOP = (touchTarget - lineHeight.label.sm) / 2;
const ACTION_SIDE_SLOP = (touchTarget - spacing.lg) / 2;
const HALF_GAP = spacing.md / 2;
const OUTER_SLOP = touchTarget - iconSize.sm - HALF_GAP;
const ACTION_ALONE_HIT_SLOP = {
  top: ACTION_VERTICAL_SLOP,
  bottom: ACTION_VERTICAL_SLOP,
  left: ACTION_SIDE_SLOP,
  right: ACTION_SIDE_SLOP,
};
const ACTION_BESIDE_CLOSE_HIT_SLOP = {
  top: ACTION_VERTICAL_SLOP,
  bottom: ACTION_VERTICAL_SLOP,
  left: OUTER_SLOP,
  right: HALF_GAP,
};
const CLOSE_HIT_SLOP = CLOSE_SLOP;
const CLOSE_BESIDE_ACTION_HIT_SLOP = {
  top: CLOSE_SLOP,
  bottom: CLOSE_SLOP,
  left: HALF_GAP,
  right: OUTER_SLOP,
};

function buildAccessibilityLabel(
  headline: string | undefined,
  subtext: string | undefined,
): string {
  return [headline, subtext].filter(Boolean).join(". ");
}

export function ToastContent({
  variant = "neutral",
  headline,
  subtext,
  action,
  icon = true,
  customIcon,
  dismissible = true,
  closeLabel = "Close notification",
  onDismiss,
  testID,
}: ToastContentProps) {
  const leading = getLeadingIconSpec(variant, customIcon);
  const a11yLabel = buildAccessibilityLabel(headline, subtext);
  const liveRegion = variant === "error" ? "assertive" : "polite";
  // Center the row to a single line of text; top-align when subtext wraps the row.
  const rowAlignment = subtext ? "items-start" : "items-center";

  return (
    // 343px (a 375px phone minus 16px each side), never wider than the slot.
    // A width, not just a max: on desktop web the engine's slot wraps its content.
    <Box
      className={cn(
        "shadow-bigcard rounded-radius-lg gap-spacing-md p-spacing-md md:p-spacing-lg bg-luce-neutral-background-default box-border flex w-[343px] max-w-full flex-row self-center",
        rowAlignment,
      )}
      accessibilityRole={variant === "error" ? "alert" : "status"}
      accessibilityLabel={a11yLabel}
      {...platformProps({ testID, slot: "toast-content", liveRegion })}
    >
      {/* Figma: 8 from the icon to the text, 12 from there on */}
      <Box className={cn("gap-spacing-sm flex min-w-0 flex-1 flex-row", rowAlignment)}>
        {icon ? (
          <Box className="shrink-0">
            <Icon
              name={leading.name}
              size={iconSize.md}
              weight="fill"
              className={leading.className}
            />
          </Box>
        ) : null}

        <Box className="gap-spacing-xs flex min-w-0 flex-1 flex-col">
          {headline ? (
            <Typography variant="label-sm" color="neutral-foreground-default">
              {headline}
            </Typography>
          ) : null}
          {subtext ? (
            <Typography variant="caption-md" color="neutral-foreground-intermediate">
              {subtext}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {action || dismissible ? (
        <Box className="gap-spacing-md flex shrink-0 flex-row items-center">
          {action ? (
            <Pressable
              accessibilityLabel={action.label}
              accessibilityRole="button"
              hitSlop={dismissible ? ACTION_BESIDE_CLOSE_HIT_SLOP : ACTION_ALONE_HIT_SLOP}
              className={cn(
                // At least 16 wide (spacing-lg), so a one-character label still makes up 44
                "min-w-spacing-lg relative inline-flex items-center justify-center",
                minTouchTargetClassName,
                // Beside the close: 22 to the left (44 − 16 − 6) and half the gap, 6, to the right
                dismissible &&
                  "[@media(pointer:coarse)]:before:-left-[calc(theme(minHeight.touch-target)-theme(width.icon-sm)-theme(spacing.spacing-md)/2)] [@media(pointer:coarse)]:before:-right-[calc(theme(spacing.spacing-md)/2)]",
              )}
              onClick={action.onClick}
            >
              <Typography variant="label-sm" color="brand-primary" className="text-center">
                {action.label}
              </Typography>
            </Pressable>
          ) : null}
          {dismissible ? (
            <Pressable
              accessibilityLabel={closeLabel}
              accessibilityRole="button"
              hitSlop={action ? CLOSE_BESIDE_ACTION_HIT_SLOP : CLOSE_HIT_SLOP}
              className={cn(
                // Its own box at the icon's size, 16 (20 on desktop), a circle (Ardhi Q4)
                "h-icon-sm w-icon-sm md:h-icon-md md:w-icon-md rounded-radius-full relative inline-flex shrink-0 items-center justify-center",
                minTouchTargetClassName,
                action &&
                  "[@media(pointer:coarse)]:before:-left-[calc(theme(spacing.spacing-md)/2)] [@media(pointer:coarse)]:before:-right-[calc(theme(minHeight.touch-target)-theme(width.icon-sm)-theme(spacing.spacing-md)/2)]",
              )}
              onClick={onDismiss}
            >
              <Icon
                name="X"
                size="sm"
                weight="regular"
                className="text-luce-neutral-foreground-intermediate"
              />
            </Pressable>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
